import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Skeleton, Space, Statistic, Tag, Typography } from "antd";
import {
  BarChartOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  RiseOutlined,
  SolutionOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import { olreadiaService } from "services/olreadiaService";
import "./OlreadiaFlowPages.css";

const { Paragraph, Text, Title } = Typography;

const countStatus = (items, statuses) => {
  const set = new Set(statuses.map((value) => String(value).toUpperCase()));
  return items.filter((item) => set.has(String(item.status || "").toUpperCase())).length;
};

const content = (page) => (Array.isArray(page?.content) ? page.content : Array.isArray(page) ? page : []);
const total = (page) => (typeof page?.totalElements === "number" ? page.totalElements : content(page).length);

const reportStats = async ({ actorId, workspaceId, mode }) => {
  const hiring = mode === "HIRING";
  const jobsPage = hiring
    ? await olreadiaService.jobOpportunities.listEmployer({ actorId, workspaceId, page: 0, size: 80 })
    : { content: [], totalElements: 0 };
  const applicationsPage = hiring
    ? await olreadiaService.jobApplications.listPostedJobs({ actorId, workspaceId, page: 0, size: 100 })
    : await olreadiaService.jobApplications.listMine({ actorId, userId: actorId, workspaceId, page: 0, size: 100 });
  const interviewsPage = hiring
    ? await olreadiaService.interviewSessions.listForWorkspace({ actorId, workspaceId, page: 0, size: 100 })
    : await olreadiaService.interviewSessions.listForUser({ actorId, userId: actorId, workspaceId, page: 0, size: 100 });

  const jobs = content(jobsPage);
  const applications = content(applicationsPage);
  const interviews = content(interviewsPage);
  const pendingApplications = countStatus(applications, ["SUBMITTED", "PENDING", "APPLIED", "UNDER_REVIEW", "SCREENING"]);
  const acceptedApplications = countStatus(applications, ["ACCEPTED", "SELECTED", "HIRED", "OFFER_ACCEPTED"]);
  const rejectedApplications = countStatus(applications, ["REJECTED", "DECLINED", "WITHDRAWN"]);
  const needsReview = pendingApplications + jobs.filter((job) => String(job.status || "").toUpperCase() === "DRAFT" || job.questionsRequired === true || job.hasQuestions === false).length;

  return {
    generatedAt: new Date().toISOString(),
    stats: {
      jobActivity: hiring ? total(jobsPage) : total(applicationsPage),
      interviews: total(interviewsPage),
      hiringProgress: hiring ? acceptedApplications : pendingApplications,
      needsReview,
    },
    sections: [
      {
        id: "jobs",
        title: hiring ? "Jobs Report" : "Applications Report",
        description: hiring ? "Track job post activity and applicant flow in this workspace." : "Track applications you submitted in this workspace.",
        metrics: hiring
          ? [
              ["Total job posts", total(jobsPage)],
              ["Open jobs", countStatus(jobs, ["OPEN"])],
              ["Draft jobs", countStatus(jobs, ["DRAFT"])],
              ["Closed jobs", countStatus(jobs, ["CLOSED", "ARCHIVED"])],
            ]
          : [
              ["Applications submitted", total(applicationsPage)],
              ["Pending review", pendingApplications],
              ["Accepted or selected", acceptedApplications],
              ["Closed applications", rejectedApplications],
            ],
      },
      {
        id: "interviews",
        title: "Interviews Report",
        description: hiring ? "Review interview sessions scheduled for jobs in this workspace." : "Review interview sessions connected to your applications.",
        metrics: [
          ["Total interviews", total(interviewsPage)],
          ["Scheduled", countStatus(interviews, ["SCHEDULED"])],
          ["Completed", countStatus(interviews, ["COMPLETED"])],
          ["Cancelled", countStatus(interviews, ["CANCELLED", "CANCELED"])],
        ],
      },
      {
        id: "hiring",
        title: hiring ? "Hiring Process Report" : "Job Search Progress Report",
        description: hiring ? "See candidate movement and follow-up priorities." : "See where your current applications stand.",
        metrics: [
          [hiring ? "Applications received" : "Applications", total(applicationsPage)],
          ["Needs review or response", pendingApplications],
          [hiring ? "Selected or hired" : "Positive outcomes", acceptedApplications],
          ["Needs attention", needsReview],
        ],
      },
    ],
  };
};

const statCards = [
  ["jobActivity", "Job activity", <SolutionOutlined />, "good"],
  ["interviews", "Interviews", <CalendarOutlined />, "active"],
  ["hiringProgress", "Progress", <RiseOutlined />, "warn"],
  ["needsReview", "Needs review", <ExclamationCircleOutlined />, "bad"],
];

export default function Reports() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedWorkspaceId, selectedWorkspace, selectedWorkspaceUse, isEmployerWorkspace } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const mode = isEmployerWorkspace || selectedWorkspaceUse === "HIRING" ? "HIRING" : "JOB_SEARCH";
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!actorId || !selectedWorkspaceId) {
      setSummary(null);
      setError(!selectedWorkspaceId ? "Select a workspace to view reports." : "");
      return;
    }
    try {
      setLoading(true);
      setError("");
      setSummary(await reportStats({ actorId, workspaceId: selectedWorkspaceId, mode }));
    } catch (nextError) {
      setError(nextError?.message || "Failed to load reports.");
    } finally {
      setLoading(false);
    }
  }, [actorId, mode, selectedWorkspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => summary?.stats || {}, [summary]);

  return (
    <div className="olreadia-flow-page">
      <div className="olreadia-flow-layout">
        <main className="olreadia-flow-main">
          <section className="olreadia-flow-hero">
            <span className="olreadia-flow-hero__icon"><BarChartOutlined /></span>
            <div>
              <Text className="olreadia-flow-eyebrow">Reports</Text>
              <Title level={2}>{mode === "HIRING" ? "Hiring reports and stats" : "Job search reports and stats"}</Title>
              <Paragraph>{selectedWorkspace?.name || "Current workspace"} • {mode === "HIRING" ? "Hiring workspace" : "Job search workspace"}</Paragraph>
              {summary?.generatedAt ? <Text>Updated {new Date(summary.generatedAt).toLocaleString()}</Text> : null}
            </div>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>Refresh</Button>
          </section>

          {error ? <Alert type="error" showIcon message="Unable to load reports" description={error} style={{ marginBottom: 12 }} /> : null}
          {loading && !summary ? <Card className="olreadia-flow-card"><Skeleton active paragraph={{ rows: 6 }} /></Card> : null}

          <div className="olreadia-report-stat-grid">
            {statCards.map(([key, label, icon, tone]) => (
              <Card key={key} className="olreadia-flow-card olreadia-report-stat-card">
                <span className={`olreadia-report-stat-icon is-${tone}`}>{icon}</span>
                <Statistic title={label} value={stats[key] || 0} />
              </Card>
            ))}
          </div>

          <Space direction="vertical" size={10} style={{ width: "100%", marginTop: 12 }}>
            {(summary?.sections || []).map((section) => (
              <Card key={section.id} className="olreadia-flow-card">
                <div className="olreadia-flow-card__top">
                  <span className="olreadia-flow-card__icon">{section.id === "interviews" ? <CalendarOutlined /> : section.id === "hiring" ? <TeamOutlined /> : <SolutionOutlined />}</span>
                  <div>
                    <Title level={4}>{section.title}</Title>
                    <Text>{section.description}</Text>
                  </div>
                  <Tag className="olreadia-flow-status is-active">Live</Tag>
                </div>
                <div className="olreadia-flow-detail-grid">
                  {section.metrics.map(([label, value]) => (
                    <div key={label}><Text>{label}</Text><strong>{value}</strong></div>
                  ))}
                </div>
              </Card>
            ))}
          </Space>
        </main>
        <SidePromotionSection className="olreadia-flow-promotions" />
      </div>
    </div>
  );
}
